<template>
  <section class="terrain-condition-editor">
    <label class="terrain-condition-editor__toggle">
      <input type="checkbox" :checked="enabled" :disabled="!enabled && !controllers.length" @change="toggle($event.target.checked)">
      Mostrar cuando
    </label>
    <p v-if="!enabled && !controllers.length" class="terrain-condition-editor__hint">Agregue antes una pregunta compatible y sin condición.</p>
    <div v-if="enabled" class="terrain-condition-editor__rule">
      <label>Pregunta controladora<TerrainSelect :value="value.questionId" :options="controllerOptions" @input="changeController" /></label>
      <label>Operador<TerrainSelect :value="value.operator" :options="operatorOptions" @input="changeOperator" /></label>
      <label v-if="needsValues">Valor
        <select v-if="isOptionController" :value="allowsManyValues ? value.values : (value.values || [])[0]" :multiple="allowsManyValues" @change="changeSelectedValues">
          <option v-for="option in controller.options || []" :key="option.optionId" :value="option.optionId">{{ option.label }}</option>
        </select>
        <input v-else :type="inputType" :value="displayValue" @input="changeScalar($event.target.value)">
      </label>
      <button type="button" @click="$emit('input', null)">Quitar condición</button>
    </div>
    <p v-if="enabled && controller" class="terrain-condition-editor__summary">Depende de «{{ controller.label }}».</p>
  </section>
</template>

<script>
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import { QUESTION_TYPE_SPECS } from "../../../services/terrain/validation.js";
import { defaultConditionValues, eligibleControllers } from "./conditionModel.js";

const OPERATOR_LABELS = {
  equals: "es igual a", notEquals: "es distinto de", in: "está en", notIn: "no está en",
  containsAny: "contiene cualquiera", containsAll: "contiene todas",
  greaterThan: "es mayor que", greaterOrEqual: "es mayor o igual que",
  lessThan: "es menor que", lessOrEqual: "es menor o igual que",
  isChecked: "está marcado", isNotChecked: "no está marcado",
  isEmpty: "está vacío", isNotEmpty: "no está vacío",
};

export default {
  name: "ConditionEditor",
  components: { TerrainSelect },
  props: {
    value: { type: Object, default: null }, definition: { type: Object, required: true },
    targetSectionId: { type: String, required: true }, targetQuestionId: { type: String, default: null },
  },
  computed: {
    enabled() { return !!this.value; },
    controllers() { return eligibleControllers(this.definition, this.targetSectionId, this.targetQuestionId); },
    controller() { return this.controllers.find((question) => this.value && question.questionId === this.value.questionId) || null; },
    controllerOptions() { return this.controllers.map((question) => ({ value: question.questionId, label: question.label || question.questionId })); },
    operatorOptions() {
      const spec = this.controller && QUESTION_TYPE_SPECS[this.controller.type];
      return (spec ? spec.operators : []).map((operator) => ({ value: operator, label: OPERATOR_LABELS[operator] || operator }));
    },
    needsValues() { return this.value && !["isEmpty", "isNotEmpty", "isChecked", "isNotChecked"].includes(this.value.operator); },
    isOptionController() { return this.controller && ["singleSelect", "multiSelect"].includes(this.controller.type); },
    allowsManyValues() { return this.value && ["in", "notIn", "containsAny", "containsAll"].includes(this.value.operator); },
    inputType() { return this.controller && this.controller.type === "number" ? "number" : this.controller && this.controller.type === "date" ? "date" : "text"; },
    displayValue() { return this.allowsManyValues ? (this.value.values || []).join(", ") : ((this.value.values || [""])[0]); },
  },
  methods: {
    toggle(checked) {
      if (!checked) return this.$emit("input", null);
      const controller = this.controllers[0];
      if (!controller) return;
      const operator = QUESTION_TYPE_SPECS[controller.type].operators[0];
      this.$emit("input", { questionId: controller.questionId, operator, values: defaultConditionValues(controller, operator) });
    },
    changeController(questionId) {
      const controller = this.controllers.find((question) => question.questionId === questionId);
      const operator = QUESTION_TYPE_SPECS[controller.type].operators[0];
      this.$emit("input", { questionId, operator, values: defaultConditionValues(controller, operator) });
    },
    changeOperator(operator) { this.$emit("input", { ...this.value, operator, values: defaultConditionValues(this.controller, operator) }); },
    changeScalar(raw) {
      let values = this.allowsManyValues ? raw.split(",").map((value) => value.trim()).filter(Boolean) : [raw];
      if (this.controller.type === "number") values = values.map((value) => Number(value));
      this.$emit("input", { ...this.value, values });
    },
    changeSelectedValues(event) {
      const selected = Array.from(event.target.options).filter((option) => option.selected).map((option) => option.value);
      this.$emit("input", { ...this.value, values: this.allowsManyValues ? selected : selected.slice(0, 1) });
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-condition-editor { border-top: 1px solid var(--color-line); display: grid; gap: var(--terrain-space-xs); padding-top: var(--terrain-space-sm); }
.terrain-condition-editor__toggle { align-items: center; display: flex; gap: var(--terrain-space-xs); }
.terrain-condition-editor__rule { align-items: end; display: grid; gap: var(--terrain-space-sm); grid-template-columns: repeat(3, minmax(130px, 1fr)) auto; }
.terrain-condition-editor__rule label { display: grid; gap: var(--terrain-space-xs); }
.terrain-condition-editor__rule input, .terrain-condition-editor__rule select { border: 1px solid var(--color-line); border-radius: var(--terrain-radius-md); font: inherit; padding: var(--terrain-space-xs); }
.terrain-condition-editor__hint, .terrain-condition-editor__summary { color: var(--color-ghost); font-size: var(--terrain-font-caption); margin: 0; }
@media (max-width: 800px) { .terrain-condition-editor__rule { grid-template-columns: 1fr; } }
</style>
